<template>
  <div class="order-event" v-if="orderData && integration && location">
    <div class="order-card" :class="{ expanded: isExpanded }">
      <!-- Header Section -->
      <div class="order-header" @click="toggleExpanded">
        <div class="order-header-left">
          <Avatar :img="integrationDetails.img" size="sm" class="integration-avatar" />
          <div class="order-summary">
            <div class="order-title">
              <span class="order-number">{{
                orderData.externalOrder.name || orderData.externalOrder.number
              }}</span>
              <span class="order-status-badge" :class="statusClass">{{ orderData.status }}</span>
            </div>
            <div class="order-subtitle">
              <span class="order-total"
                >${{ (orderData.amounts.grandTotalCents / 100).toFixed(2) }}</span
              >
              <span class="order-separator">•</span>
              <span class="item-count"
                >{{ orderData.items.length }} item{{
                  orderData.items.length !== 1 ? 's' : ''
                }}</span
              >
              <span class="order-separator">•</span>
              <span class="order-time">{{ created | moment('LT') }}</span>
            </div>
          </div>
        </div>
        <div class="order-header-right">
          <div class="expand-icon" :class="{ rotated: isExpanded }">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </div>
        </div>
      </div>

      <!-- Expanded Content -->
      <transition name="expand" @enter="enter" @leave="leave">
        <div v-show="isExpanded" class="order-details">
          <!-- Order Items -->
          <div class="order-section">
            <h4 class="section-title">Order Items</h4>
            <div class="items-list">
              <div
                v-for="item in orderData.items"
                :key="item.externalId || item.name"
                class="order-item"
              >
                <div class="item-main">
                  <div class="item-info">
                    <span class="item-quantity">{{ item.quantity }}×</span>
                    <span class="item-name">{{ item.name }}</span>
                    <span class="item-price">${{ (item.totalCents / 100).toFixed(2) }}</span>
                  </div>
                  <div v-if="item.modifiers && item.modifiers.length" class="item-modifiers">
                    <span
                      v-for="modifier in item.modifiers"
                      :key="modifier.externalId || modifier.name"
                      class="modifier"
                    >
                      {{ modifier.name }}
                      <span v-if="modifier.totalCents > 0"
                        >+${{ (modifier.totalCents / 100).toFixed(2) }}</span
                      >
                    </span>
                  </div>
                  <div v-if="item.note" class="item-note">
                    {{ item.note }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="order-section">
            <h4 class="section-title">Order Summary</h4>
            <div class="summary-lines">
              <div class="summary-line">
                <span>Subtotal</span>
                <span>${{ (orderData.amounts.subtotalCents / 100).toFixed(2) }}</span>
              </div>
              <div v-if="orderData.amounts.taxTotalCents > 0" class="summary-line">
                <span>Tax</span>
                <span>${{ (orderData.amounts.taxTotalCents / 100).toFixed(2) }}</span>
              </div>
              <div v-if="orderData.amounts.tipTotalCents > 0" class="summary-line">
                <span>Tip</span>
                <span>${{ (orderData.amounts.tipTotalCents / 100).toFixed(2) }}</span>
              </div>
              <div v-if="orderData.amounts.serviceChargeTotalCents > 0" class="summary-line">
                <span>Service Charge</span>
                <span>${{ (orderData.amounts.serviceChargeTotalCents / 100).toFixed(2) }}</span>
              </div>
              <div v-if="orderData.amounts.discountTotalCents > 0" class="summary-line discount">
                <span>Discount</span>
                <span>-${{ (orderData.amounts.discountTotalCents / 100).toFixed(2) }}</span>
              </div>
              <div class="summary-line total">
                <span>Total</span>
                <span>${{ (orderData.amounts.grandTotalCents / 100).toFixed(2) }}</span>
              </div>
            </div>
          </div>

          <!-- Payment Information -->
          <div v-if="orderData.tenders && orderData.tenders.length" class="order-section">
            <h4 class="section-title">Payment</h4>
            <div class="payment-methods">
              <div
                v-for="tender in orderData.tenders"
                :key="tender.externalId || tender.name"
                class="payment-method"
              >
                <div class="payment-icon">
                  <svg
                    v-if="tender.type === 'CARD'"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                  <svg
                    v-else
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div class="payment-details">
                  <span class="payment-name">{{ tender.name }}</span>
                  <span v-if="tender.card && tender.card.last4" class="card-details">
                    {{ tender.card.brand }} ••{{ tender.card.last4 }}
                  </span>
                  <span class="payment-amount">${{ (tender.amountCents / 100).toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Details -->
          <div class="order-section">
            <h4 class="section-title">Order Details</h4>
            <div class="order-meta">
              <div class="meta-item">
                <span class="meta-label">Order Type:</span>
                <span class="meta-value">{{ orderData.fulfillmentMethod }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Channel:</span>
                <span class="meta-value">{{ orderData.channel }}</span>
              </div>
              <div v-if="orderData.table && orderData.table.name" class="meta-item">
                <span class="meta-label">Table:</span>
                <span class="meta-value">{{ orderData.table.name }}</span>
              </div>
              <div v-if="orderData.externalEmployee" class="meta-item">
                <span class="meta-label">Employee ID:</span>
                <span class="meta-value">{{
                  orderData.externalEmployee.name || orderData.externalEmployee.id
                }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Placed:</span>
                <span class="meta-value">{{
                  orderData._timestamps.placedAt | moment('MMMM Do, YYYY [at] LT')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { map } from 'lodash-es'
import Avatar from '@/components/MainContent/Avatar'
import { getIntegrationDetails, availableIntegrations } from '@/lib/modules/integration'
import { createNamespacedHelpers } from 'vuex'
import { convertS3toAssetUrl } from '@/lib/format'
const IntegrationModule = createNamespacedHelpers('integration')
const IntegrationProviderModule = createNamespacedHelpers('integrationProvider')

export default {
  name: 'OrderEvent',
  props: ['created', 'integration', 'location', 'order'],
  components: {
    Avatar
  },
  data: () => ({
    integrationProviderId: null,
    isExpanded: false,
    // Dummy data based on PurchaseOrder model
    dummyOrderData: {
      status: 'COMPLETED',
      preparationStatus: 'PREPARED',
      fulfillmentMethod: 'DINE_IN',
      channel: 'POS',
      externalOrder: {
        id: 'ext_12345',
        name: 'ORDER #247',
        number: '247'
      },
      externalCustomer: {
        id: 'cust_67890',
        name: 'John Smith',
        email: 'john.smith@email.com',
        phone: '+1-555-0123'
      },
      externalEmployee: {
        id: 'emp_11111',
        name: 'Sarah Johnson',
        email: 'sarah.j@restaurant.com'
      },
      _timestamps: {
        placedAt: new Date('2024-01-15T18:30:00Z'),
        fulfilledAt: new Date('2024-01-15T19:00:00Z')
      },
      table: {
        id: 'table_5',
        name: 'Table 5',
        number: '5',
        guestCount: 2
      },
      currency: 'USD',
      amounts: {
        subtotalCents: 4250,
        discountTotalCents: 0,
        serviceChargeTotalCents: 0,
        taxTotalCents: 340,
        tipTotalCents: 850,
        grandTotalCents: 5440
      },
      items: [
        {
          name: 'Grilled Salmon',
          externalId: 'item_001',
          categoryName: 'Entrees',
          quantity: 1,
          unitPriceCents: 2800,
          totalCents: 2800,
          taxCents: 224,
          note: 'Medium rare, extra lemon',
          modifiers: [
            {
              name: 'Extra Lemon',
              group: 'Additions',
              quantity: 1,
              unitPriceCents: 0,
              totalCents: 0
            },
            {
              name: 'Side Salad',
              group: 'Sides',
              quantity: 1,
              unitPriceCents: 350,
              totalCents: 350
            }
          ]
        },
        {
          name: 'Margherita Pizza',
          externalId: 'item_002',
          categoryName: 'Pizza',
          quantity: 1,
          unitPriceCents: 1450,
          totalCents: 1450,
          taxCents: 116,
          modifiers: [
            {
              name: 'Extra Cheese',
              group: 'Toppings',
              quantity: 1,
              unitPriceCents: 200,
              totalCents: 200
            }
          ]
        }
      ],
      tenders: [
        {
          name: 'Credit Card',
          type: 'CARD',
          amountCents: 4590,
          tipCents: 850,
          card: {
            brand: 'Visa',
            last4: '1234',
            authCode: 'ABC123',
            entryMode: 'CHIP'
          }
        }
      ],
      taxes: [
        {
          name: 'Sales Tax',
          amountCents: 340,
          jurisdiction: 'CA'
        }
      ]
    }
  }),
  computed: {
    ...IntegrationProviderModule.mapGetters(['selectIntegrationProviderById']),
    integrationDetails() {
      let name = null
      if (
        this.integrationProviderId &&
        this.selectIntegrationProviderById(this.integrationProviderId)
      ) {
        let { displayName, logo } = this.selectIntegrationProviderById(this.integrationProviderId)
        const img = convertS3toAssetUrl(logo)
        return { name: displayName, img }
      } else {
        name = this.integration?.provider
      }

      return getIntegrationDetails(name) || {}
    },
    orderData() {
      // Use dummy data for now, can be replaced with real order data later
      return this.dummyOrderData
    },
    statusClass() {
      const status = this.orderData.status?.toLowerCase()
      return `status-${status}`
    }
  },
  methods: {
    ...IntegrationModule.mapActions(['getIntegration']),
    toggleExpanded() {
      this.isExpanded = !this.isExpanded
    },
    enter(el) {
      el.style.height = 'auto'
      const height = getComputedStyle(el).height
      el.style.height = '0'
      el.offsetHeight // force reflow
      el.style.transition = 'height 0.3s ease'
      el.style.height = height
    },
    leave(el) {
      el.style.transition = 'height 0.3s ease'
      el.style.height = '0'
    }
  },
  async mounted() {
    if (!Object.keys(this.integrationDetails).length) {
      const response = await this.getIntegration(this.integration._id)
      this.integrationProviderId = response.body.data.integration.integrationProvider
    }
  }
}
</script>

<style lang="scss" scoped>
.order-event {
  margin-bottom: 16px;
}

.order-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-color: #d1d5db;
  }

  &.expanded {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: #3b82f6;
  }
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }
}

.order-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.integration-avatar {
  flex-shrink: 0;
}

.order-summary {
  flex: 1;
}

.order-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.order-number {
  font-weight: 600;
  font-size: 16px;
  color: #111827;
}

.order-status-badge {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  &.status-completed {
    background-color: #d1fae5;
    color: #065f46;
  }

  &.status-pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  &.status-cancelled {
    background-color: #fee2e2;
    color: #991b1b;
  }

  &.status-preparing {
    background-color: #dbeafe;
    color: #1e40af;
  }
}

.order-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
}

.order-total {
  font-weight: 600;
  color: #111827;
}

.order-separator {
  color: #d1d5db;
}

.order-header-right {
  display: flex;
  align-items: center;
}

.expand-icon {
  color: #6b7280;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;

  &.rotated {
    transform: rotate(180deg);
  }
}

.order-details {
  border-top: 1px solid #f3f4f6;
  background-color: #fafafa;
  overflow: hidden;
}

.order-section {
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.item-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-quantity {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
}

.item-name {
  font-weight: 500;
  color: #111827;
  flex: 1;
}

.item-price {
  font-weight: 600;
  color: #059669;
}

.item-modifiers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 44px;
}

.modifier {
  background: #ede9fe;
  color: #5b21b6;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.item-note {
  margin-left: 44px;
  font-style: italic;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #d1d5db;
}

.summary-lines {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  &.discount {
    color: #dc2626;
  }

  &.total {
    background: #f9fafb;
    font-weight: 600;
    color: #111827;
    font-size: 16px;
  }
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.payment-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border-radius: 6px;
  color: #6b7280;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.payment-name {
  font-weight: 500;
  color: #111827;
}

.card-details {
  font-size: 12px;
  color: #6b7280;
}

.payment-amount {
  font-weight: 600;
  color: #059669;
  margin-left: auto;
}

.order-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.meta-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.meta-value {
  font-weight: 500;
  color: #111827;
}

// Expand animation
.expand-enter-active,
.expand-leave-active {
  transition: height 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}

// Responsive design
@media (max-width: 768px) {
  .order-header {
    padding: 12px;
  }

  .order-section {
    padding: 16px;
  }

  .order-meta {
    grid-template-columns: 1fr;
  }

  .item-info {
    flex-wrap: wrap;
  }

  .item-modifiers,
  .item-note {
    margin-left: 0;
  }
}
</style>
